import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

interface PanelGroupProps {
  title: string;
  /** Riassunto mostrato accanto al titolo: deve parlare anche a gruppo chiuso. */
  meta?: React.ReactNode;
  /** Contenuto allineato a destra nell'intestazione (importi, badge di avviso). */
  right?: React.ReactNode;
  defaultOpen?: boolean;
  /** Passare `open` + `onOpenChange` rende il gruppo controllato dal genitore. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * Gruppo collassabile del pannello riga servizio: chevron magenta, titolo
 * uppercase, riassunto a fianco. Stessa grammatica dei gruppi del drawer
 * Nota/Attività — nessuna cornice, la gerarchia la fa la tipografia.
 *
 * Stato interno (non controllato): `defaultOpen` decide solo l'apertura
 * iniziale, così un gruppo aperto dall'utente non si richiude da solo al
 * primo re-render del pannello.
 */
export function PanelGroup({
  title,
  meta,
  right,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
}: PanelGroupProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-2 py-1.5 -mx-2 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors text-left"
      >
        <ChevronRight
          className={`h-3 w-3 flex-shrink-0 text-[#E91E8C] transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="text-[11px] uppercase tracking-wide font-semibold text-[#E91E8C]">
          {title}
        </span>
        {meta != null && (
          <span className="text-[12px] text-black/40 dark:text-white/40 tabular-nums truncate">
            {meta}
          </span>
        )}
        {right != null && (
          <span className="ml-auto flex items-center gap-2 flex-shrink-0">{right}</span>
        )}
      </button>
      {open && <div className="pl-5 pt-1 pb-2">{children}</div>}
    </div>
  );
}
